/**
 * Kanban Board View Component
 *
 * Displays expenditure requests organized by status in a Kanban board layout:
 * - Draft, Pending, Approved, Rejected, Cancelled columns
 * - Request cards with key information
 * - Filter controls for category and date range
 * - Click-to-view detail functionality
 */

import { renderIcon } from '../utils/icons';
import { createSidebarLayout } from '../components/sidebar-layout';
import { apiClient } from '../services/api-client';
import {
  subscribeToAuth,
  selectUser,
  selectIsAuthenticated,
  type AuthState,
} from '../services/auth-store';

/**
 * Request status types for Kanban columns
 */
type RequestStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

/**
 * Expenditure category types
 */
type ExpenditureCategory =
  | 'TRAVEL'
  | 'EQUIPMENT'
  | 'SOFTWARE'
  | 'TRAINING'
  | 'MARKETING'
  | 'OFFICE_SUPPLIES'
  | 'PROFESSIONAL_SERVICES'
  | 'OTHER';

/**
 * Expenditure request from API
 */
interface ExpenditureRequest {
  id: string;
  title: string;
  description: string;
  amount: number;
  currency: string;
  status: RequestStatus;
  category?: ExpenditureCategory;
  created_at: string;
  updated_at: string;
  created_by: string;
  creator_name?: string | null;
  creator_email?: string | null;
}

/**
 * Column configuration for the Kanban board
 */
interface KanbanColumn {
  status: RequestStatus;
  label: string;
  icon: string;
  colorClass: string;
  bgClass: string;
}

/**
 * Kanban column configurations
 */
const KANBAN_COLUMNS: KanbanColumn[] = [
  {
    status: 'DRAFT',
    label: 'Draft',
    icon: 'edit',
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
  },
  {
    status: 'PENDING',
    label: 'Pending',
    icon: 'clock',
    colorClass: 'text-warning',
    bgClass: 'bg-warning/20',
  },
  {
    status: 'APPROVED',
    label: 'Approved',
    icon: 'checkCircle',
    colorClass: 'text-success',
    bgClass: 'bg-success/20',
  },
  {
    status: 'REJECTED',
    label: 'Rejected',
    icon: 'xCircle',
    colorClass: 'text-destructive',
    bgClass: 'bg-destructive/20',
  },
  {
    status: 'CANCELLED',
    label: 'Cancelled',
    icon: 'x',
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
  },
];

/**
 * Category display names and colors
 */
const CATEGORY_CONFIG: Record<ExpenditureCategory, { label: string; color: string }> = {
  TRAVEL: { label: 'Travel', color: 'bg-blue-500/20 text-blue-400' },
  EQUIPMENT: { label: 'Equipment', color: 'bg-purple-500/20 text-purple-400' },
  SOFTWARE: { label: 'Software', color: 'bg-cyan-500/20 text-cyan-400' },
  TRAINING: { label: 'Training', color: 'bg-green-500/20 text-green-400' },
  MARKETING: { label: 'Marketing', color: 'bg-pink-500/20 text-pink-400' },
  OFFICE_SUPPLIES: { label: 'Office', color: 'bg-amber-500/20 text-amber-400' },
  PROFESSIONAL_SERVICES: { label: 'Services', color: 'bg-indigo-500/20 text-indigo-400' },
  OTHER: { label: 'Other', color: 'bg-neutral-500/20 text-neutral-400' },
};

/**
 * Format currency amount
 */
function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format relative time
 */
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-CH', { month: 'short', day: 'numeric' });
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Create a request card element for the Kanban board
 */
function createRequestCard(request: ExpenditureRequest): HTMLElement {
  const card = document.createElement('a');
  card.href = `/requests/${request.id}`;
  card.className =
    'block glass-sm p-4 rounded-lg cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg hover:border-primary/30';
  card.setAttribute('data-request-id', request.id);

  // Get category config
  const categoryConfig = request.category
    ? CATEGORY_CONFIG[request.category]
    : { label: 'Uncategorized', color: 'bg-neutral-500/20 text-neutral-400' };

  // Build card content
  card.innerHTML = `
    <div class="space-y-3">
      <!-- Title -->
      <h4 class="text-sm font-medium text-foreground line-clamp-2">${escapeHtml(request.title || 'Untitled')}</h4>

      <!-- Amount and Category -->
      <div class="flex items-center justify-between gap-2">
        <span class="text-lg font-semibold text-foreground">${formatAmount(request.amount || 0, request.currency || 'CHF')}</span>
        <span class="px-2 py-0.5 text-xs font-medium rounded-full ${categoryConfig.color}">
          ${categoryConfig.label}
        </span>
      </div>

      <!-- Timestamp -->
      <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
        ${renderIcon('clock', { size: 'xs', className: 'text-muted-foreground' }).outerHTML}
        <span>${formatRelativeTime(request.created_at)}</span>
      </div>

      <!-- Created by (if available) -->
      ${
        request.creator_name
          ? `
        <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
          ${renderIcon('user', { size: 'xs', className: 'text-muted-foreground' }).outerHTML}
          <span class="truncate">${escapeHtml(request.creator_name)}</span>
        </div>
      `
          : ''
      }
    </div>
  `;

  // Handle click navigation
  card.addEventListener('click', (e) => {
    e.preventDefault();
    window.history.pushState({}, '', `/requests/${request.id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  });

  return card;
}

/**
 * Create a Kanban column element
 */
function createKanbanColumn(
  column: KanbanColumn,
  requests: ExpenditureRequest[]
): HTMLElement {
  const columnEl = document.createElement('div');
  // Mobile: full width with snap, Desktop: flexible width
  columnEl.className = 'flex flex-col w-[85vw] sm:w-[280px] sm:min-w-[280px] sm:max-w-[320px] flex-shrink-0 sm:flex-1';
  columnEl.setAttribute('data-status', column.status);

  // Column header
  const header = document.createElement('div');
  header.className = 'flex items-center gap-2 p-3 rounded-t-xl glass-sm';
  header.innerHTML = `
    <div class="w-8 h-8 rounded-lg ${column.bgClass} flex items-center justify-center">
      ${renderIcon(column.icon as Parameters<typeof renderIcon>[0], { size: 'sm', className: column.colorClass }).outerHTML}
    </div>
    <h3 class="font-medium text-foreground">${column.label}</h3>
    <span class="ml-auto text-sm font-medium ${column.colorClass}" data-count="${column.status}">
      ${requests.length}
    </span>
  `;

  // Column content with scrollable area
  const content = document.createElement('div');
  content.className =
    'flex-1 p-2 space-y-3 overflow-y-auto max-h-[calc(100vh-280px)] glass-sm rounded-b-xl border-t border-border/50';
  content.id = `kanban-column-${column.status.toLowerCase()}`;

  if (requests.length === 0) {
    content.innerHTML = `
      <div class="flex flex-col items-center justify-center py-8 text-center">
        <div class="w-12 h-12 rounded-full ${column.bgClass} flex items-center justify-center mb-3">
          ${renderIcon(column.icon as Parameters<typeof renderIcon>[0], { size: 'md', className: column.colorClass }).outerHTML}
        </div>
        <p class="text-sm text-muted-foreground">No ${column.label.toLowerCase()} requests</p>
      </div>
    `;
  } else {
    requests.forEach((request) => {
      content.appendChild(createRequestCard(request));
    });
  }

  columnEl.appendChild(header);
  columnEl.appendChild(content);

  return columnEl;
}

/**
 * Create filter controls section
 */
function createFilterControls(
  onFilterChange: (filters: { category?: string; fromDate?: string; toDate?: string }) => void
): HTMLElement {
  const filters = document.createElement('div');
  filters.className = 'flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 p-4 glass-sm rounded-xl mb-6';

  // Category filter
  const categoryGroup = document.createElement('div');
  categoryGroup.className = 'flex flex-col sm:flex-row sm:items-center gap-2';
  categoryGroup.innerHTML = `
    <label class="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
      ${renderIcon('filter', { size: 'sm', className: 'text-muted-foreground' }).outerHTML}
      Category
    </label>
    <select id="filter-category" class="px-3 py-2 sm:py-1.5 rounded-lg text-sm text-foreground bg-background/50 border border-border touch-target">
      <option value="">All Categories</option>
      <option value="TRAVEL">Travel</option>
      <option value="EQUIPMENT">Equipment</option>
      <option value="SOFTWARE">Software</option>
      <option value="TRAINING">Training</option>
      <option value="MARKETING">Marketing</option>
      <option value="OFFICE_SUPPLIES">Office Supplies</option>
      <option value="PROFESSIONAL_SERVICES">Professional Services</option>
      <option value="OTHER">Other</option>
    </select>
  `;

  // Date range filter
  const dateGroup = document.createElement('div');
  dateGroup.className = 'flex flex-col sm:flex-row items-stretch sm:items-center gap-2';
  dateGroup.innerHTML = `
    <label class="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
      ${renderIcon('calendar', { size: 'sm', className: 'text-muted-foreground' }).outerHTML}
      Date Range
    </label>
    <div class="flex items-center gap-2">
      <input type="date" id="filter-from-date" class="px-3 py-2 sm:py-1.5 rounded-lg text-sm text-foreground bg-background/50 border border-border flex-1 touch-target" placeholder="From" />
      <span class="text-muted-foreground">to</span>
      <input type="date" id="filter-to-date" class="px-3 py-2 sm:py-1.5 rounded-lg text-sm text-foreground bg-background/50 border border-border flex-1 touch-target" placeholder="To" />
    </div>
  `;

  // Clear filters button
  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className =
    'sm:ml-auto flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors glass-button rounded-lg touch-target';
  clearBtn.innerHTML = `
    ${renderIcon('x', { size: 'sm' }).outerHTML}
    <span>Clear Filters</span>
  `;

  filters.appendChild(categoryGroup);
  filters.appendChild(dateGroup);
  filters.appendChild(clearBtn);

  // Event listeners
  const categorySelect = filters.querySelector('#filter-category') as HTMLSelectElement;
  const fromDateInput = filters.querySelector('#filter-from-date') as HTMLInputElement;
  const toDateInput = filters.querySelector('#filter-to-date') as HTMLInputElement;

  const triggerFilter = () => {
    const filters: { category?: string; fromDate?: string; toDate?: string } = {};
    if (categorySelect.value) {
      filters.category = categorySelect.value;
    }
    if (fromDateInput.value) {
      filters.fromDate = fromDateInput.value;
    }
    if (toDateInput.value) {
      filters.toDate = toDateInput.value;
    }
    onFilterChange(filters);
  };

  categorySelect.addEventListener('change', triggerFilter);
  fromDateInput.addEventListener('change', triggerFilter);
  toDateInput.addEventListener('change', triggerFilter);

  clearBtn.addEventListener('click', () => {
    categorySelect.value = '';
    fromDateInput.value = '';
    toDateInput.value = '';
    onFilterChange({});
  });

  return filters;
}

/**
 * Create the Kanban board view
 */
export function createKanbanView(): HTMLElement {
  // Create main content
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6';

  // Page title section
  const titleSection = document.createElement('div');
  titleSection.className = 'mb-6';
  titleSection.innerHTML = `
    <p class="text-muted-foreground" id="kanban-subtitle">View and manage your expenditure requests by status.</p>
  `;

  // Subscribe to update title with user name
  const unsubscribe = subscribeToAuth((state: AuthState) => {
    const user = selectUser(state);
    const isAuthenticated = selectIsAuthenticated(state);
    if (isAuthenticated && user) {
      const subtitle = document.getElementById('kanban-subtitle');
      if (subtitle) {
        subtitle.textContent = `${user.givenName || user.displayName}'s expenditure requests organized by status.`;
      }
    }
  });

  // Store unsubscribe for cleanup
  (content as HTMLElement & { cleanup?: () => void }).cleanup = unsubscribe;

  // Filter controls
  let currentFilters: { category?: string; fromDate?: string; toDate?: string } = {};
  const filterControls = createFilterControls((filters) => {
    currentFilters = filters;
    loadKanbanData(kanbanBoard, currentFilters);
  });

  // Kanban board container with touch-friendly scrolling
  const kanbanBoard = document.createElement('div');
  kanbanBoard.id = 'kanban-board';
  kanbanBoard.className = 'flex gap-4 overflow-x-auto pb-4 touch-scroll snap-x snap-proximity -mx-4 px-4 sm:mx-0 sm:px-0';

  // Loading state
  const loadingPlaceholder = document.createElement('div');
  loadingPlaceholder.className = 'flex gap-4 w-full';
  loadingPlaceholder.id = 'kanban-loading';

  KANBAN_COLUMNS.forEach(() => {
    const placeholder = document.createElement('div');
    placeholder.className = 'flex flex-col w-[85vw] sm:w-[280px] sm:min-w-[280px] sm:max-w-[320px] flex-shrink-0 sm:flex-1 animate-pulse';
    placeholder.innerHTML = `
      <div class="h-14 glass-sm rounded-t-xl"></div>
      <div class="flex-1 p-2 space-y-3 glass-sm rounded-b-xl">
        <div class="h-28 bg-muted rounded-lg"></div>
        <div class="h-28 bg-muted rounded-lg"></div>
        <div class="h-28 bg-muted rounded-lg"></div>
      </div>
    `;
    loadingPlaceholder.appendChild(placeholder);
  });

  kanbanBoard.appendChild(loadingPlaceholder);

  // Assemble content
  content.appendChild(titleSection);
  content.appendChild(filterControls);
  content.appendChild(kanbanBoard);

  // Load initial data - pass the element directly since it's not yet in the DOM
  loadKanbanData(kanbanBoard);

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/kanban',
    title: 'Kanban Board',
  });
}

/**
 * Load Kanban board data from API
 * @param kanbanBoard - The kanban board element to populate (passed directly to avoid DOM timing issues)
 * @param filters - Optional filters for category and date range
 */
async function loadKanbanData(
  kanbanBoard: HTMLElement,
  filters: { category?: string; fromDate?: string; toDate?: string } = {}
): Promise<void> {
  if (!kanbanBoard) return;

  // Show loading state - query within the element, not the document
  const loadingEl = kanbanBoard.querySelector('#kanban-loading');
  if (!loadingEl) {
    // Add loading state if not present
    const loadingPlaceholder = document.createElement('div');
    loadingPlaceholder.className = 'flex gap-4 w-full';
    loadingPlaceholder.id = 'kanban-loading';

    KANBAN_COLUMNS.forEach(() => {
      const placeholder = document.createElement('div');
      placeholder.className = 'flex flex-col w-[85vw] sm:w-[280px] sm:min-w-[280px] sm:max-w-[320px] flex-shrink-0 sm:flex-1 animate-pulse';
      placeholder.innerHTML = `
        <div class="h-14 glass-sm rounded-t-xl"></div>
        <div class="flex-1 p-2 space-y-3 glass-sm rounded-b-xl">
          <div class="h-28 bg-muted rounded-lg"></div>
          <div class="h-28 bg-muted rounded-lg"></div>
        </div>
      `;
      loadingPlaceholder.appendChild(placeholder);
    });

    kanbanBoard.innerHTML = '';
    kanbanBoard.appendChild(loadingPlaceholder);
  }

  try {
    // Build query params
    // Use onlyMine=true to show only user's own requests
    const params: Record<string, string | number | boolean | undefined> = {
      limit: 100, // Get enough items to populate all columns
      onlyMine: true, // Filter to user's own requests
    };

    if (filters.category) {
      params.category = filters.category;
    }
    if (filters.fromDate) {
      params.fromDate = filters.fromDate;
    }
    if (filters.toDate) {
      params.toDate = filters.toDate;
    }

    const response = await apiClient.get<{
      data: ExpenditureRequest[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>('/requests', { params });

    const requests = response.data.data;

    // Group requests by status
    const requestsByStatus: Record<RequestStatus, ExpenditureRequest[]> = {
      DRAFT: [],
      PENDING: [],
      APPROVED: [],
      REJECTED: [],
      CANCELLED: [],
    };

    requests.forEach((request) => {
      if (requestsByStatus[request.status]) {
        requestsByStatus[request.status].push(request);
      }
    });

    // Render columns
    kanbanBoard.innerHTML = '';

    KANBAN_COLUMNS.forEach((column) => {
      const columnRequests = requestsByStatus[column.status];
      kanbanBoard.appendChild(createKanbanColumn(column, columnRequests));
    });
  } catch (error) {
    console.error('Failed to load kanban data:', error);

    // Show error state
    kanbanBoard.innerHTML = `
      <div class="flex-1 glass-sm p-8 rounded-xl text-center">
        <div class="flex justify-center mb-4">
          ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
        </div>
        <p class="text-muted-foreground mb-4">Failed to load requests</p>
        <button onclick="window.location.reload()" class="text-primary hover:text-primary/80 text-sm">
          Refresh page
        </button>
      </div>
    `;
  }
}

/**
 * Kanban view function for router
 */
export function kanbanView(): HTMLElement {
  return createKanbanView();
}

export default kanbanView;
